import { Tooltip, TooltipContent, TooltipTrigger } from "@myela/ui";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { useIsTruncated } from "@/hooks/useIsTruncated";
import { cn } from "@/lib/utils";

// Literal classes so Tailwind's scanner emits them — `line-clamp-${n}` gets purged.
const CLAMP: Record<number, string> = {
    1: "block truncate",
    2: "line-clamp-2",
    3: "line-clamp-3",
    4: "line-clamp-4",
    5: "line-clamp-5",
    6: "line-clamp-6",
};

type TruncateOwnProps = {
    children: ReactNode;
    /** `false` renders the text in full — no clamp, no measuring, no tooltip. */
    truncate?: boolean;
    /** 1 = single-line ellipsis, >1 = multi-line clamp. */
    lines?: number;
    /** Tooltip body; defaults to `children`. */
    tooltip?: ReactNode;
    /** Clamp without ever revealing a tooltip. */
    showTooltip?: boolean;
    tooltipSide?: "top" | "right" | "bottom" | "left";
    tooltipAlign?: "start" | "center" | "end";
    tooltipClassName?: string;
    /** Make the clipped text a tab stop; turn off inside an already-focusable row or button. */
    focusable?: boolean;
    className?: string;
};

export type TruncateProps<T extends ElementType = "span"> = TruncateOwnProps & {
    /** Render as a different element — "div", "p", "h3"… */
    as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof TruncateOwnProps | "as">;

/**
 * Clamps its text and reveals the full value in a tooltip **only when the clamp
 * actually clipped something** — short values render no tooltip at all.
 *
 * The clamp needs a bounded width to bite: `min-w-0` on a flex parent, or
 * `max-w-[Npx]` / a fixed table layout inside a table cell. Single-line mode
 * renders `block`; pass `inline-block` in `className` to sit inline with text.
 */
export function Truncate<T extends ElementType = "span">({
    children,
    truncate = true,
    lines = 1,
    tooltip,
    showTooltip = true,
    tooltipSide = "top",
    tooltipAlign,
    tooltipClassName,
    focusable = true,
    as,
    className,
    ...rest
}: TruncateProps<T>) {
    const Component = (as ?? "span") as ElementType;
    const { ref, isTruncated } = useIsTruncated<HTMLElement>({ enabled: truncate, lines });

    if (!truncate) {
        return (
            <Component className={className} {...rest}>
                {children}
            </Component>
        );
    }

    // The trigger always renders so resolving the measurement never remounts the element.
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Component
                    ref={ref}
                    className={cn(CLAMP[lines] ?? CLAMP[6], className)}
                    tabIndex={focusable && isTruncated ? 0 : undefined}
                    {...rest}
                >
                    {children}
                </Component>
            </TooltipTrigger>
            {isTruncated && showTooltip ? (
                <TooltipContent
                    side={tooltipSide}
                    align={tooltipAlign}
                    className={cn("break-words", tooltipClassName)}
                >
                    {tooltip ?? children}
                </TooltipContent>
            ) : null}
        </Tooltip>
    );
}
